import { html, View } from 'rune-ts';
import klass from './Toast.module.scss';
import { Typo } from '../../atom';

export interface ToastData {
  label: string;
  close_label: string;
}

export interface ToastOption {
  delay?: number;
}

export class Toast extends View<ToastData> {
  toast_container?: HTMLElement;

  constructor(
    data: ToastData,
    public option: ToastOption = {},
  ) {
    super(data);
  }

  override template() {
    return html`<div class="${klass.toast_container} ${Typo.typo.p1}"></div> `;
  }

  override onRender() {
    if (!document.body.querySelector(`.${klass.toast_container}`)) {
      document.body.appendChild(this.element());
    }

    this.toast_container = document.body.querySelector(`.${klass.toast_container}`)!;

    this.delegate('click', `.${klass.close_button}`, (e) => {
      const target = e.target as HTMLButtonElement;
      target.closest(`.${klass.toast}`)?.remove();

      if (this.empty_toast) {
        this.toast_container?.remove();
      }
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        this.updatePosition();
      });

      window.visualViewport.addEventListener('scroll', () => {
        if (this.has_on_screen_keyboard) {
          this.updatePosition();
        }
      });
    }
  }

  private get has_on_screen_keyboard() {
    if (window.visualViewport) {
      return window.innerHeight > window.visualViewport.height;
    }
    return false;
  }

  private updatePosition() {
    if (window.visualViewport) {
      const body = document.querySelector('body');
      if (body) {
        const layout_viewport = body.getBoundingClientRect();
        const lvph = layout_viewport.height.toFixed(2);
        const vvh = window.visualViewport.height;
        const vvb = window.visualViewport.offsetTop + vvh;

        this.element().style.bottom = `calc(${lvph ?? '0px'}px - ${vvb ?? '0px'}px + ${this.has_on_screen_keyboard ? '2rem' : '78px'})`;
      }
    }
  }

  private get empty_toast() {
    return this.toast_container?.querySelectorAll(`.${klass.toast}`).length === 0;
  }

  messageNode() {
    const el = document.createElement('div');
    el.classList.add(klass.toast);
    el.innerHTML = `<span class="${klass.message}">${this.data.label}</span><button type="button" class="${klass.close_button}">${this.data.close_label}</button>`;
    return el;
  }

  hideMessage(toast_element: HTMLDivElement) {
    toast_element.classList.add(klass.hidden);
    toast_element.addEventListener('animationend', () => {
      if (this.toast_container!.contains(toast_element)) {
        this.toast_container!.removeChild(toast_element);
      }
      if (this.empty_toast) {
        this.toast_container?.remove();
      }
    });
  }

  scheduleHiding(toast_element) {
    setTimeout(() => this.hideMessage(toast_element), this.option.delay ?? 5000);
  }

  open() {
    this.render();
    const new_toast = this.messageNode();
    requestAnimationFrame(() => {
      this.toast_container?.appendChild(new_toast);
    });

    this.scheduleHiding(new_toast);
  }

  static open(data: ToastData, option: ToastOption) {
    const toast = new Toast(data, option);
    return toast.open();
  }
}
